import React, { useLayoutEffect, useRef } from 'react';
import { useFocusTrap } from '@omq/shared/src/hooks/use-focus-trap';

interface ConfirmDialogProps {
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  // Element to restore focus to when the dialog closes. The dialog opens in
  // the same React batch that unmounts the menu item that triggered it, so
  // document.activeElement is already <body> at mount time — the opener must
  // be captured explicitly by the caller.
  returnFocusEl?: HTMLElement | null;
}

/**
 * ConfirmDialog component
 * Renders an in-window confirmation dialog with a semi-transparent overlay backdrop.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  returnFocusEl,
}) => {
  const messageId = useRef(`omq-confirm-msg-${Math.random().toString(36).slice(2)}`);

  // Shared trap (shadow-root aware): scopes Tab within the dialog, focuses the
  // first tabbable element on mount, and calls onCancel on Escape.
  const focusTrapRef = useFocusTrap(true, onCancel);

  // Restore focus to the opener on close (useLayoutEffect: restore before
  // paint, per the #2468 convention). Mount focus is owned by the trap.
  useLayoutEffect(() => {
    return () => {
      returnFocusEl?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    // Click-to-dismiss backdrop: the keyboard equivalent is Escape (useFocusTrap),
    // so no key handler belongs on the overlay itself — standard modal pattern.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <div
      className="omq-chatbot-confirm-overlay"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby={messageId.current}>
      {/* stopPropagation shield so clicks inside the dialog don't dismiss it */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="omq-chatbot-confirm-dialog"
        ref={focusTrapRef}
        onClick={(e) => e.stopPropagation()}>
        <p id={messageId.current} className="omq-chatbot-confirm-message">
          {message}
        </p>
        <div className="omq-chatbot-confirm-actions">
          <button
            className="omq-chatbot-confirm-btn omq-chatbot-confirm-btn--secondary"
            onClick={onCancel}
            type="button">
            {cancelLabel}
          </button>
          <button
            className="omq-chatbot-confirm-btn omq-chatbot-confirm-btn--primary"
            onClick={onConfirm}
            type="button">
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
